import React from "react";
import { Box, Typography } from "@mui/material";
import logo from "../../images/songjukti_logo.png";
import { tokens, fadeInUp } from "./tokens";

/* Shared, brand-consistent styles for every auth screen -------------- */

export const primaryButtonSx = {
  height: tokens.button.height.lg,
  px: `${tokens.button.paddingX.lg}px`,
  borderRadius: `${tokens.button.radius}px`,
  bgcolor: tokens.color.teal,
  color: tokens.color.white,
  fontSize: tokens.button.fontSize.lg,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: tokens.color.tealDark, boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: tokens.color.line, color: tokens.color.inkFaint },
};

export const ghostButtonSx = {
  height: tokens.button.height.lg,
  px: `${tokens.button.paddingX.md}px`,
  borderRadius: `${tokens.button.radius}px`,
  color: tokens.color.inkMuted,
  fontSize: tokens.button.fontSize.lg,
  fontWeight: 600,
  textTransform: "none",
  border: `1px solid ${tokens.color.line}`,
  "&:hover": {
    bgcolor: tokens.bg.surfaceMuted,
    borderColor: tokens.input.borderHover,
  },
};

export const fieldSx = {
  borderRadius: `${tokens.input.radius}px`,
  bgcolor: tokens.input.bg,
  fontSize: tokens.input.fontSize.md,
  "& fieldset": { borderColor: tokens.input.border },
  "&:hover fieldset": { borderColor: `${tokens.input.borderHover} !important` },
  "&.Mui-focused fieldset": { borderColor: `${tokens.input.borderFocus} !important` },
};

export const labelSx = {
  fontSize: "0.875rem",
  fontWeight: 600,
  color: tokens.color.ink,
  mb: 0.5,
};

export const linkSx = {
  color: tokens.color.teal,
  fontWeight: 600,
  textDecoration: "none",
  "&:hover": { color: tokens.color.tealDark, textDecoration: "underline" },
};

/* Page shell --------------------------------------------------------- */

const AuthShell = ({ title, subtitle, children, footer, maxWidth = 460 }) => (
  <Box
    sx={{
      minHeight: "100dvh",
      bgcolor: tokens.bg.page,
      backgroundImage: `radial-gradient(circle at 12% 0%, ${tokens.color.tealLight}, transparent 42%)`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      px: 2,
      py: { xs: 3, md: 6 },
    }}
  >
    <Box
      component="a"
      href="https://songjukti.com/"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        mb: 3,
        textDecoration: "none",
      }}
    >
      <Box component="img" src={logo} alt="" sx={{ width: 40, height: 40 }} />
      <Typography
        sx={{
          fontFamily: tokens.font.heading,
          fontWeight: 700,
          fontSize: "1.25rem",
          letterSpacing: "0.04em",
          color: tokens.color.teal,
        }}
      >
        SONGJUKTI
      </Typography>
    </Box>

    <Box
      sx={{
        width: "100%",
        maxWidth,
        bgcolor: tokens.bg.surface,
        borderRadius: `${tokens.radius.lg}px`,
        border: `1px solid ${tokens.color.line}`,
        boxShadow: tokens.shadow.md,
        p: { xs: 2.5, sm: 4 },
        ...fadeInUp,
        animation: "fadeInUp .4s ease both",
      }}
    >
      {(title || subtitle) && (
        <Box sx={{ textAlign: "center", mb: 3 }}>
          {title && (
            <Typography variant="h4" sx={{ color: tokens.color.ink }}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              variant="body2"
              sx={{ color: tokens.color.inkMuted, mt: 0.75 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      {children}
    </Box>

    {footer && <Box sx={{ mt: 3, textAlign: "center" }}>{footer}</Box>}
  </Box>
);

export default AuthShell;